import { ReadFile } from '@paragon/client-tools';
import { PlatePrice } from '@paragon/contract-library';
import { designService, NewPlatePrice } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { setPlatePrices } from '../store/platePricesSlice';
import { setPlateTypeProperties } from '../store/plateTypePropertiesSlice';
import { AsyncThunkActionBoolean, AsyncThunkActionVoid } from './thunkActions';

export const getAllPlatePrices = (): AsyncThunkActionVoid => async (dispatch) => {
  const spinnerId = dispatch(addSpinnerMessage('Loading Plate Prices'));

  try {
    dispatch(setPlatePrices(await designService.platePrices.getAllPlatePrices()));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const getPlateTypeProperties = (): AsyncThunkActionVoid => async (dispatch) => {
  const defaultPlateStockSizes = await designService.platePairs.getAllPlateTypeProperties();
  dispatch(setPlateTypeProperties(defaultPlateStockSizes));
};

export const importPlatePrices =
  (file: ReadFile): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Importing Plate Prices'));
    try {
      const platePriceRecords = await designService.platePrices.importPlatePrices(file);
      dispatch(setPlatePrices(platePriceRecords));
    } catch (exception) {
      if ('message' in (exception as Error)) {
        dispatch(enqueueError((exception as Error).message));
      } else {
        dispatch(enqueueError('Error importing'));
      }
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const insertPlatePrice =
  (newPlatePrice: NewPlatePrice): AsyncThunkActionBoolean =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Adding Plate Price'));

    try {
      const addedPlatePrice = await designService.platePrices.postPlatePrice(newPlatePrice);
      dispatch(setPlatePrices([addedPlatePrice]));
      return true;
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error inserting plate price'));
      return false;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const editPlatePrices =
  (platePriceRecords: readonly PlatePrice[]): AsyncThunkActionBoolean =>
  async (dispatch) => {
    const spinnerId = dispatch(
      addSpinnerMessage(
        platePriceRecords.length === 1 ? 'Editing Price' : `Editing ${platePriceRecords.length} Plate Prices`,
      ),
    );

    try {
      const edits = platePriceRecords.map(({ guid, pricePerPound, notes }) => ({ guid, pricePerPound, notes }));
      const newPriceRecords = await designService.platePrices.putPlatePrices(edits);
      dispatch(setPlatePrices(newPriceRecords));
      return true;
    } catch {
      dispatch(
        enqueueError(
          platePriceRecords.length === 1
            ? 'Error editing plate price.'
            : `Error editing ${platePriceRecords.length} plate prices.`,
        ),
      );
      return false;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
